/** Bounded, retryable Burnout input streaming and the device's best survival scores. */

import {
  BURNOUT_VERSION,
  type BurnoutScore,
  burnoutPhysics,
  MAX_BURNOUT_CHUNK_BYTES,
  MAX_BURNOUT_CHUNK_TICKS,
} from "@lache/relay/burnout";
import { isExplicitName } from "@lache/relay/names";
import { type InputFrame, quantizeFrame, sameFrame, type TapeFrame } from "@lache/sim";
import { cleanName, MAX_NAME_LENGTH } from "./scores";

export type BurnoutEntry = BurnoutScore;
export type BurnoutResult = Pick<BurnoutEntry, "obstacles" | "time" | "duration">;
export type BurnoutSyncState =
  | "idle"
  | "recording"
  | "queued"
  | "saved"
  | "rejected"
  | "overflow"
  | "disabled";
export interface BurnoutStatus {
  state: BurnoutSyncState;
  message: string;
}
export interface BurnoutScoresOptions {
  storage?: Storage | null;
  fetch?: typeof fetch;
  timeoutMs?: number;
  onlineTarget?: EventTarget | null;
  isOnline?: () => boolean;
  onStatus?: (status: BurnoutStatus) => void;
}

const MAX_QUEUE_BYTES = 4 * 1024 * 1024;
const MAX_PENDING_MESSAGES = 512;
const LOCAL_LIMIT = 40;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const LOCAL_KEY = `lache.burnout.scores.v${BURNOUT_VERSION}`;
const RELOAD_MESSAGE = "Burnout has changed. Reload to play the new course.";

interface Message {
  runId: string;
  kind: "begin" | "chunk" | "cancel";
  sequence: number;
  createdAt: number;
  body: string;
  final?: boolean;
}
interface ActiveRun {
  id: string;
  name: string;
  token: string;
  tuning: string;
  createdAt: number;
  sequence: number;
  ticks: number;
  frames: TapeFrame[];
  finalized: boolean;
  blocked: boolean;
  sent: boolean;
}

function storageOrNull(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}
function validDate(value: unknown): value is number {
  return finite(value) && Number.isSafeInteger(value) && value <= 8.64e15;
}
function validResult(value: unknown): value is BurnoutResult & Record<string, unknown> {
  return (
    record(value) &&
    finite(value.obstacles) &&
    Number.isSafeInteger(value.obstacles) &&
    finite(value.time) &&
    finite(value.duration) &&
    value.time <= value.duration
  );
}
function validEntry(value: unknown): value is BurnoutEntry {
  return (
    validResult(value) &&
    record(value) &&
    typeof value.name === "string" &&
    value.name.trim().length > 0 &&
    value.name.length <= MAX_NAME_LENGTH &&
    value.version === BURNOUT_VERSION &&
    typeof value.tuning === "string" &&
    value.tuning.length <= 128 &&
    validDate(value.date) &&
    (value.achievedAt === undefined || validDate(value.achievedAt))
  );
}
function publicEntry(entry: BurnoutEntry): BurnoutEntry {
  return {
    name: cleanName(entry.name),
    obstacles: entry.obstacles,
    time: entry.time,
    duration: entry.duration,
    date: entry.date,
    version: entry.version,
    tuning: entry.tuning,
    ...(entry.achievedAt === undefined ? {} : { achievedAt: entry.achievedAt }),
  };
}
export function compareBurnout(a: BurnoutEntry, b: BurnoutEntry): number {
  return b.obstacles - a.obstacles || a.time - b.time || a.date - b.date;
}
function token(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const data = crypto.getRandomValues(new Uint8Array(16));
  data[6] = (data[6]! & 0x0f) | 0x40;
  data[8] = (data[8]! & 0x3f) | 0x80;
  const hex = [...data].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
function size(value: string): number {
  return new TextEncoder().encode(value).length;
}
function messageId(message: Message): string {
  return `${message.runId}:${message.kind === "cancel" ? "cancel" : message.sequence}`;
}
function validMessage(value: unknown): value is Message {
  if (
    !record(value) ||
    typeof value.runId !== "string" ||
    !UUID.test(value.runId) ||
    !["begin", "chunk", "cancel"].includes(String(value.kind)) ||
    !Number.isSafeInteger(value.sequence) ||
    (value.sequence as number) < -1 ||
    !validDate(value.createdAt) ||
    typeof value.body !== "string" ||
    size(value.body) > MAX_BURNOUT_CHUNK_BYTES
  )
    return false;
  try {
    const body: unknown = JSON.parse(value.body);
    if (!record(body) || typeof body.playerToken !== "string" || !UUID.test(body.playerToken)) return false;
    if (value.kind === "cancel") return true;
    if (value.kind === "begin")
      return (
        value.sequence === -1 &&
        body.runId === value.runId &&
        body.version === BURNOUT_VERSION &&
        typeof body.name === "string" &&
        typeof body.tuningHash === "string"
      );
    if (
      body.version !== BURNOUT_VERSION ||
      body.sequence !== value.sequence ||
      !Array.isArray(body.frames) ||
      !body.frames.length
    )
      return false;
    let ticks = 0;
    for (const frame of body.frames) {
      if (!Array.isArray(frame) || frame.length !== 8 || !frame.every(Number.isInteger)) return false;
      ticks += frame[0];
      if (
        frame[0] < 1 ||
        ticks > MAX_BURNOUT_CHUNK_TICKS ||
        frame.slice(1, 5).some((axis) => axis < -127 || axis > 127) ||
        frame[5] < 0 ||
        frame[5] > 255 ||
        frame[6] < 0 ||
        frame[6] > 255 ||
        frame[7] < 0 ||
        frame[7] > 15
      )
        return false;
    }
    return true;
  } catch {
    return false;
  }
}

export class BurnoutScores {
  readonly enabled: boolean;
  private readonly baseUrl: string;
  private readonly storage: Storage | null;
  private readonly requestFetch: typeof fetch;
  private readonly timeoutMs: number;
  private readonly onlineTarget: EventTarget | null;
  private readonly isOnline: () => boolean;
  private readonly onStatus: (status: BurnoutStatus) => void;
  private readonly prefix: string;
  private readonly identityKey: string;
  private readonly queue = new Map<string, Message>();
  private readonly controllers = new Set<AbortController>();
  private local: BurnoutEntry[] = [];
  private playerToken: string | null = null;
  private active: ActiveRun | null = null;
  private lastRunId: string | null = null;
  private latestResultRunId: string | null = null;
  private resultStatus: BurnoutStatus | null = null;
  private flushing: Promise<void> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retries = 0;
  private disposed = false;
  private layoutRetired = false;
  private storageReadable = true;
  private storageWritable = true;
  private durableQueue = true;
  private currentStatus: BurnoutStatus = { state: "idle", message: "" };
  private readonly online = (): void => {
    void this.flush();
  };

  constructor(baseUrl: string | null = null, options: BurnoutScoresOptions = {}) {
    this.baseUrl = baseUrl?.replace(/\/+$/, "") ?? "";
    this.enabled = /^https?:\/\//.test(this.baseUrl);
    this.storage = options.storage === undefined ? storageOrNull() : options.storage;
    this.durableQueue = this.storage !== null;
    this.requestFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.timeoutMs = options.timeoutMs ?? 10000;
    this.onlineTarget =
      options.onlineTarget === undefined
        ? typeof window === "undefined"
          ? null
          : window
        : options.onlineTarget;
    this.isOnline =
      options.isOnline ?? (() => typeof navigator === "undefined" || navigator.onLine !== false);
    this.onStatus = options.onStatus ?? (() => {});
    this.prefix = `lache.burnout.pending.v${BURNOUT_VERSION}:${this.baseUrl}:`;
    this.identityKey = `lache.global.players.v1:${this.baseUrl}`;
    this.readLocal();
    if (this.enabled) {
      this.syncQueue();
      this.onlineTarget?.addEventListener("online", this.online);
      void this.flush();
    }
  }

  get status(): BurnoutStatus {
    if (this.currentStatus.state === "recording" && this.resultStatus) return this.resultStatus;
    return this.currentStatus;
  }
  get pendingCount(): number {
    return new Set([...this.queue.values()].map((message) => message.runId)).size;
  }
  /** A copy keeps UI sorting and formatting from changing persisted rankings. */
  localBoard(): BurnoutEntry[] {
    const names = new Set<string>();
    return this.local
      .filter((entry) => {
        if (!burnoutPhysics(entry.tuning) || names.has(entry.name)) return false;
        names.add(entry.name);
        return true;
      })
      .map(publicEntry);
  }
  private setStatus(state: BurnoutSyncState, message: string): void {
    if (this.currentStatus.state === state && this.currentStatus.message === message) return;
    this.currentStatus = { state, message };
    if (this.lastRunId === this.latestResultRunId) this.resultStatus = this.currentStatus;
    this.onStatus(this.status);
  }
  private resultSync(runId: string, state: BurnoutSyncState, message: string): void {
    if (this.lastRunId === runId) {
      this.setStatus(state, message);
    } else if (this.latestResultRunId === runId) {
      this.resultStatus = { state, message };
      this.onStatus(this.status);
    }
  }
  private readLocal(): void {
    try {
      const raw = this.storage?.getItem(LOCAL_KEY);
      if (!raw) return;
      const value: unknown = JSON.parse(raw);
      if (Array.isArray(value)) this.local = [...this.local, ...value.filter(validEntry).map(publicEntry)];
    } catch {
      // A damaged or unavailable store does not prevent a new attempt.
    }
    this.local = this.bestPerPlayer(this.local);
  }
  private bestPerPlayer(entries: BurnoutEntry[]): BurnoutEntry[] {
    const names = new Set<string>();
    return entries
      .sort(compareBurnout)
      .filter((entry) => {
        const key = `${entry.tuning}:${entry.name}`;
        if (names.has(key)) return false;
        names.add(key);
        return true;
      })
      .slice(0, LOCAL_LIMIT);
  }
  private identity(): string {
    let identities: Record<string, unknown> = {};
    try {
      const value: unknown = JSON.parse(this.storage?.getItem(this.identityKey) ?? "{}");
      if (record(value)) identities = value;
    } catch {
      // Reuse this tab's identity when browser storage is unavailable.
    }
    const existing = identities["0"];
    this.playerToken =
      typeof existing === "string" && UUID.test(existing) ? existing : (this.playerToken ?? token());
    identities["0"] = this.playerToken;
    try {
      this.storage?.setItem(this.identityKey, JSON.stringify(identities));
    } catch {
      // Identity remains stable until the tab closes.
    }
    return this.playerToken;
  }

  begin(name: string, tuningHash: string): void {
    this.abandon();
    if (this.disposed) return;
    const run: ActiveRun = {
      id: token(),
      name: cleanName(name),
      token: this.enabled ? this.identity() : "",
      tuning: burnoutPhysics(tuningHash)?.hash ?? tuningHash,
      createdAt: Date.now(),
      sequence: 0,
      ticks: 0,
      frames: [],
      finalized: false,
      blocked: !this.enabled || this.layoutRetired || isExplicitName(name),
      sent: false,
    };
    this.active = run;
    this.lastRunId = run.id;
    if (!this.enabled) {
      this.setStatus("disabled", "Scores save on this device.");
      return;
    }
    if (run.blocked) {
      this.setStatus(
        "rejected",
        this.layoutRetired ? RELOAD_MESSAGE : "Choose a different player name to submit a global score.",
      );
      return;
    }
    this.setStatus("recording", "");
    this.enqueue({
      runId: run.id,
      kind: "begin",
      sequence: -1,
      createdAt: run.createdAt,
      body: JSON.stringify({
        runId: run.id,
        playerToken: run.token,
        name: run.name,
        version: BURNOUT_VERSION,
        tuningHash,
      }),
    });
    void this.flush();
  }

  /** Called with precisely the same rounded input as the simulation, once per actual tick. */
  push(frame: InputFrame): void {
    const run = this.active;
    if (!run || run.blocked || run.finalized || this.disposed) return;
    const encoded = quantizeFrame(frame);
    const previous = run.frames.at(-1);
    if (previous && sameFrame(previous, encoded)) previous[0]++;
    else run.frames.push(encoded);
    run.ticks++;
    if (run.ticks === MAX_BURNOUT_CHUNK_TICKS) this.seal();
  }

  finish(result: BurnoutResult, achievedAt = Date.now()): BurnoutEntry {
    const run = this.active;
    if (!run || run.finalized || !validResult(result) || !validDate(achievedAt))
      throw new Error("No valid Burnout attempt to finish");
    run.finalized = true;
    this.latestResultRunId = run.id;
    this.resultStatus = this.currentStatus;
    const entry: BurnoutEntry = {
      name: run.name,
      ...result,
      date: achievedAt,
      achievedAt,
      tuning: run.tuning,
      version: BURNOUT_VERSION,
    };
    this.readLocal();
    this.local = this.bestPerPlayer([...this.local, entry]);
    try {
      this.storage?.setItem(LOCAL_KEY, JSON.stringify(this.local));
    } catch {
      // Local results still last for the current tab.
    }
    if (!run.blocked) {
      this.setStatus(
        "queued",
        this.durableQueue
          ? "Waiting to save globally…"
          : "Keep this game open while your global score syncs.",
      );
      this.seal(achievedAt);
      // A failure exactly on a chunk boundary was already sealed by push(). Mark its queued
      // envelope without altering the input bytes of a request that may already be in flight.
      const terminal = this.queue.get(`${run.id}:${run.sequence - 1}`);
      if (terminal) {
        terminal.final = true;
        this.persist(terminal);
      }
      void this.flush();
    }
    return publicEntry(entry);
  }

  /** A manual restart or leaving the course never submits an unfinished score. */
  abandon(): void {
    const run = this.active;
    if (!run) return;
    this.active = null;
    if (run.finalized || run.blocked) return;
    this.dropRun(run.id);
    if (this.enabled && !this.disposed && run.sent) {
      this.enqueue({
        runId: run.id,
        kind: "cancel",
        sequence: run.sequence,
        createdAt: run.createdAt,
        body: JSON.stringify({ playerToken: run.token }),
      });
      void this.flush();
    }
  }

  private seal(achievedAt?: number): void {
    const run = this.active;
    if (!run || run.blocked || !run.frames.length) return;
    const message: Message = {
      runId: run.id,
      kind: "chunk",
      sequence: run.sequence++,
      createdAt: run.createdAt,
      body: JSON.stringify({
        playerToken: run.token,
        version: BURNOUT_VERSION,
        sequence: run.sequence - 1,
        frames: run.frames,
        ...(achievedAt === undefined ? {} : { achievedAt }),
      }),
      ...(achievedAt === undefined ? {} : { final: true }),
    };
    run.frames = [];
    run.ticks = 0;
    this.enqueue(message);
    void this.flush();
  }

  private syncQueue(): void {
    if (!this.storageReadable || this.layoutRetired) return;
    try {
      let total = [...this.queue.values()].reduce((sum, item) => sum + size(JSON.stringify(item)), 0);
      for (let i = 0; i < (this.storage?.length ?? 0); i++) {
        const key = this.storage!.key(i);
        if (!key?.startsWith(this.prefix)) continue;
        const id = key.slice(this.prefix.length);
        if (this.queue.has(id)) continue;
        const raw = this.storage!.getItem(key);
        if (!raw || raw.length > MAX_QUEUE_BYTES) continue;
        try {
          const value: unknown = JSON.parse(raw);
          if (!validMessage(value) || messageId(value) !== id) continue;
          const length = size(raw);
          if (this.queue.size >= MAX_PENDING_MESSAGES || total + length > MAX_QUEUE_BYTES) continue;
          total += length;
          this.queue.set(id, value);
        } catch {
          // One damaged message cannot discard a different player's pending run.
        }
      }
    } catch {
      // The bounded in-memory queue remains available if storage disappears.
    }
  }
  private enqueue(message: Message): void {
    this.syncQueue();
    const serialized = JSON.stringify(message);
    const total = [...this.queue.values()].reduce((sum, item) => sum + size(JSON.stringify(item)), 0);
    if (this.queue.size >= MAX_PENDING_MESSAGES || total + size(serialized) > MAX_QUEUE_BYTES) {
      const run = this.active;
      if (run?.id === message.runId && message.kind !== "cancel") {
        run.blocked = true;
        run.frames = [];
        this.dropRun(run.id);
        if (run.sent)
          this.enqueue({
            runId: run.id,
            kind: "cancel",
            sequence: run.sequence,
            createdAt: run.createdAt,
            body: JSON.stringify({ playerToken: run.token }),
          });
        this.setStatus("overflow", "Global score buffer full. This run saves on this device only.");
      }
      return;
    }
    const id = messageId(message);
    this.queue.set(id, message);
    this.persist(message);
  }
  private persist(message: Message): void {
    if (!this.storageWritable) return;
    try {
      this.storage?.setItem(`${this.prefix}${messageId(message)}`, JSON.stringify(message));
    } catch {
      // A full disk still permits bounded retries in the open tab.
      this.durableQueue = false;
      this.storageWritable = false;
      if (this.lastRunId === message.runId)
        this.setStatus("queued", "Keep this game open while your global score syncs.");
    }
  }
  private forget(message: Message): void {
    const id = messageId(message);
    this.queue.delete(id);
    // Acknowledged chunks need no memory for an endless run, even when storage breaks.
    try {
      this.storage?.removeItem(`${this.prefix}${id}`);
    } catch {
      // Avoid reimporting undeletable old packets or retaining an unbounded tombstone set.
      this.storageReadable = false;
      this.storageWritable = false;
      this.durableQueue = false;
    }
  }
  private dropRun(runId: string): void {
    for (const message of this.queue.values()) if (message.runId === runId) this.forget(message);
  }

  flush(): Promise<void> {
    this.clearRetry();
    if (!this.enabled || this.disposed || this.layoutRetired || !this.isOnline()) return Promise.resolve();
    if (this.flushing) return this.flushing;
    this.syncQueue();
    if (!this.queue.size) return Promise.resolve();
    this.flushing = this.flushQueue().finally(() => {
      this.flushing = null;
      if (this.queue.size && this.retryTimer === null && !this.disposed && this.isOnline()) void this.flush();
    });
    return this.flushing;
  }
  private async flushQueue(): Promise<void> {
    while (!this.disposed && this.isOnline()) {
      const message = [...this.queue.values()].sort(
        (a, b) => a.createdAt - b.createdAt || a.runId.localeCompare(b.runId) || a.sequence - b.sequence,
      )[0];
      if (!message) return;
      try {
        if (this.active?.id === message.runId) this.active.sent = true;
        const response = await this.request(
          message.kind === "begin" ? "/burnout/runs" : `/burnout/runs/${message.runId}`,
          message.body,
          message.kind === "cancel" ? "DELETE" : "POST",
        );
        if (this.disposed) return;
        if (message.kind === "cancel" && response.status >= 200 && response.status < 300) {
          this.forget(message);
          continue;
        }
        const body = response.body;
        const accepted =
          response.status >= 200 &&
          response.status < 300 &&
          record(body) &&
          body.runId === message.runId &&
          typeof body.nextSequence === "number" &&
          Number.isSafeInteger(body.nextSequence) &&
          body.nextSequence >= message.sequence + 1 &&
          typeof body.failed === "boolean" &&
          validResult(body) &&
          (!body.failed || validEntry(body.entry));
        if (accepted) {
          this.forget(message);
          for (const older of this.queue.values())
            if (
              older.runId === message.runId &&
              older.kind !== "cancel" &&
              older.sequence < (body.nextSequence as number)
            )
              this.forget(older);
          this.retries = 0;
          if (message.final && !body.failed) {
            this.dropRun(message.runId);
            this.resultSync(message.runId, "rejected", "This run could not enter the global leaderboard.");
            continue;
          }
          if (body.failed) {
            this.dropRun(message.runId);
            if (!(this.lastRunId === message.runId && this.currentStatus.state === "overflow"))
              this.resultSync(message.runId, "saved", "Saved to the global leaderboard.");
          }
          continue;
        }
        if (
          response.status === 410 &&
          record(body) &&
          body.error === "Burnout has changed; reload and run again"
        ) {
          // Keep retired recordings under their original revision keys. A new build ignores
          // them; this old tab must stop retrying or deleting them while it asks for a reload.
          this.layoutRetired = true;
          if (this.active) this.active.blocked = true;
          this.queue.clear();
          this.clearRetry();
          this.setStatus("rejected", RELOAD_MESSAGE);
          return;
        }
        if (
          response.status >= 400 &&
          response.status < 500 &&
          response.status !== 408 &&
          response.status !== 429
        ) {
          this.dropRun(message.runId);
          if (this.active?.id === message.runId) this.active.blocked = true;
          if (message.kind !== "cancel")
            this.resultSync(message.runId, "rejected", "This run could not enter the global leaderboard.");
          continue;
        }
        this.scheduleRetry(response.retryAfterMs);
        return;
      } catch {
        if (this.disposed) return;
        this.scheduleRetry();
        return;
      }
    }
  }
  private clearRetry(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }
  private scheduleRetry(retryAfter = 0): void {
    if (this.disposed || !this.isOnline() || !this.queue.size) return;
    this.clearRetry();
    const delay = Math.max(retryAfter, Math.min(60000, 1000 * 2 ** Math.min(this.retries++, 6)));
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.flush();
    }, delay);
  }

  async load(): Promise<BurnoutEntry[]> {
    if (!this.enabled || this.disposed) return [];
    const response = await this.request("/burnout/leaderboard");
    const body = response.body;
    if (
      response.status < 200 ||
      response.status >= 300 ||
      !record(body) ||
      body.version !== BURNOUT_VERSION ||
      !Array.isArray(body.entries) ||
      body.entries.length > 10 ||
      !body.entries.every(validEntry)
    )
      throw new Error("The Burnout leaderboard is unavailable");
    return body.entries.map(publicEntry).sort(compareBurnout);
  }
  private async request(
    path: string,
    body?: string,
    method = "GET",
  ): Promise<{ status: number; body: unknown; retryAfterMs: number }> {
    const controller = new AbortController();
    this.controllers.add(controller);
    const timeout = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await this.requestFetch(`${this.baseUrl}${path}`, {
        method,
        credentials: "omit",
        signal: controller.signal,
        ...(body === undefined ? {} : { body, headers: { "Content-Type": "application/json" } }),
      });
      let parsed: unknown = null;
      try {
        parsed = await response.json();
      } catch {
        // A truncated successful response is retried with the same sequence and exact inputs.
      }
      const retryHeader = response.headers.get("Retry-After");
      const retryAfterMs = retryHeader === null ? 0 : Number(retryHeader) * 1000;
      return {
        status: response.status,
        body: parsed,
        retryAfterMs: Number.isFinite(retryAfterMs) && retryAfterMs > 0 ? retryAfterMs : 0,
      };
    } finally {
      clearTimeout(timeout);
      this.controllers.delete(controller);
    }
  }
  dispose(): void {
    this.disposed = true;
    this.clearRetry();
    this.onlineTarget?.removeEventListener("online", this.online);
    for (const controller of this.controllers) controller.abort();
    this.controllers.clear();
    this.active = null;
  }
}
